import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Subject as SubjectIcon,
  Add as AddIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';
import { usePhase } from '../../../contexts/PhaseContext';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';
import { levelLabel } from '../../../config/pageCatalogue.generated';

const SubjectManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activePhase } = usePhase();

  const resolvedPhaseCode = (() => {
    try {
      const fromCtx = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      return String(fromCtx).trim().toLowerCase();
    } catch (_) {
      return '';
    }
  })();
  const isSecPhase = resolvedPhaseCode === 'sec' || resolvedPhaseCode === 'secondary';

  const getDisplayName = (user) => {
    if (!user) return '';
    const clean = (v) => {
      const s = (v ?? '').toString().trim();
      if (!s) return '';
      const low = s.toLowerCase();
      if (low === 'undefined' || low === 'null' || low === 'nan') return '';
      return s;
    };
    return (
      clean(user.displayName || user.preferredName) ||
      clean(user.fullName || user.full_name) ||
      (() => {
        const first = clean(user.firstName || user.first_name);
        const last = clean(user.lastName || user.last_name);
        return [first, last].filter(Boolean).join(' ').trim();
      })() ||
      clean(user.name) ||
      clean(user.username) ||
      clean(user.email) ||
      ''
    );
  };
  
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOverviewData();
  }, [activePhase]);

  const fetchOverviewData = async () => {
    try {
      setError(null);
      setLoading(true);
      // Context + cache
      let schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      // If schoolId missing, resolve from /api/users/me
      if (!schoolId) {
        try {
          const me = await fetchJson('/api/users/me');
          if (me && me.schoolId) {
            schoolId = String(me.schoolId);
            try { localStorage.setItem('scopeSchoolId', schoolId); } catch(_) {}
          }
        } catch(_) {}
      }
      const phaseCodeLower = (phaseCode || '').toLowerCase();
      const phaseCodeToId = (code) => {
        if (!code) return null;
        const normalized = code.toString().toLowerCase();
        if (normalized === 'sec' || normalized === 'secondary') return 2;
        if (normalized === 'nur_pri' || normalized === 'nursery' || normalized === 'nursery_primary') return 1;
        const parsed = parseInt(normalized, 10);
        return Number.isInteger(parsed) ? parsed : null;
      };
      const resolvedPhaseId = Number.isInteger(parseInt(phaseId, 10))
        ? parseInt(phaseId, 10)
        : phaseCodeToId(phaseCodeLower);
      const ctxKey = `${schoolId}|${resolvedPhaseId || phaseCodeLower || '-'}|${ayId || '-'}`;
      const kSubjects = `subjMgmt:subjects:${ctxKey}`;
      const kClasses = `subjMgmt:classes:${ctxKey}`;
      const kTeachers = `subjMgmt:teachers:${ctxKey}`;
      let prefilled = false;
      try { const r = localStorage.getItem(kSubjects); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setSubjects(v); if (v.length) prefilled = true; } } } catch(_) {}
      try { const r = localStorage.getItem(kClasses); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setClasses(v); if (v.length) prefilled = true; } } } catch(_) {}
      try { const r = localStorage.getItem(kTeachers); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setTeachers(v); if (v.length) prefilled = true; } } } catch(_) {}
      if (prefilled) setLoading(false);
      let maxGuard; try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}

      // Live fetch in parallel (context-aware headers; only include non-empty values)
      const headers = {};
      if (schoolId) headers['X-Scope-School-Id'] = schoolId;
      if (phaseCode) headers['X-Active-Phase'] = phaseCode;
      if (ayId) headers['X-Academic-Year-Id'] = ayId;
      if (phaseId) headers['X-Active-Phase-Id'] = phaseId;
      // Prefer headers for scoping; also pass phase and schoolId in query when present (align with other pages)
      const qsTail = [
        phaseCode ? `phase=${encodeURIComponent(phaseCode)}` : '',
        schoolId ? `schoolId=${encodeURIComponent(schoolId)}` : ''
      ].filter(Boolean).join('&');

      // The level check keys off the phase CODE, but the phase is often known
      // only as an id — the switcher sets X-Active-Phase-Id without always
      // setting the code. When that happened phaseCodeLower was '', so
      // forbiddenLevels came out empty and the level check quietly did nothing
      // while the phaseId check still passed.
      //
      // That matters here because a phaseId is not sufficient on its own: this
      // school has eight subjects whose level is first_cycle or second_cycle
      // stored against phase 1 (nursery/primary). They pass a phaseId test, so
      // the overview listed 16 subjects for nursery and primary, half of them
      // secondary work. Deriving the code back from the id keeps the level
      // check alive whichever of the two the caller supplied.
      const codeFromPhaseId = (id) => (id === 2 ? 'sec' : id === 1 ? 'nur_pri' : '');
      const effectivePhaseCode = phaseCodeLower || codeFromPhaseId(resolvedPhaseId);

      const filterByPhaseContext = (items = []) => {
        if (!Array.isArray(items)) return [];
        if (!resolvedPhaseId && !effectivePhaseCode) return items;
        const nurPriLevels = ['nursery', 'primary'];
        const secLevels = ['first_cycle', 'second_cycle'];
        const forbiddenLevels = effectivePhaseCode === 'nur_pri' ? secLevels
          : effectivePhaseCode === 'sec' ? nurPriLevels : [];
        return items.filter((item) => {
          const lvl = (item?.level || '').toString().trim().toLowerCase();
          if (lvl && forbiddenLevels.includes(lvl)) return false;
          const candidateId = Number(item?.phaseId ?? item?.PhaseId ?? item?.phase_id);
          if (Number.isInteger(candidateId)) {
            // With no id resolved, the level check above has already had its
            // say; rejecting on an unknown id would empty the page.
            return resolvedPhaseId ? candidateId === resolvedPhaseId : true;
          }
          const candidateCode = (item?.phaseCode ?? item?.Phase?.code ?? item?.phase_code ?? '').toString().toLowerCase();
          if (candidateCode) return candidateCode === effectivePhaseCode;
          return false;
        });
      };

      // 1) Classes: try classes-lite (returns { success, classes }) then /api/classes (returns array)
      let classesArray = [];
      try {
        const liteUrl = `/api/classes-lite${qsTail ? `?${qsTail}` : ''}`;
        const lite = await fetchJson(liteUrl, { headers });
        classesArray = Array.isArray(lite?.classes) ? lite.classes : (Array.isArray(lite) ? lite : []);
      } catch (_) {}
      if (!Array.isArray(classesArray) || classesArray.length === 0) {
        try {
          const clsUrl = `/api/classes${qsTail ? `?${qsTail}` : ''}`;
          const cls = await fetchJson(clsUrl, { headers });
          classesArray = Array.isArray(cls) ? cls : (cls?.classes || cls?.data || []);
        } catch (_) {}
      }
      classesArray = filterByPhaseContext(classesArray);

      // 2) Teachers: use /api/users and filter Teachers
      let teachersArray = [];
      let usersArray = [];
      try {
        const usersUrl = `/api/users${qsTail ? `?${qsTail}` : ''}`;
        const usersResp = await fetchJson(usersUrl, { headers });
        const rows = Array.isArray(usersResp?.rows) ? usersResp.rows : (Array.isArray(usersResp) ? usersResp : []);
        teachersArray = rows.filter(u => {
          const roleName = (u.role || u.roleName || '').toString().toLowerCase();
          const roleId = Number(u.roleId || u.RoleId || u.role_id || 0);
          return roleName === 'teacher' || roleId === 3;
        });
        usersArray = rows;
      } catch (_) {
        teachersArray = [];
      }

      // 3) Subjects: try allocations endpoint (robust, phase-aware) then fallbacks
      let subjectsArray = [];
      try {
        const allocScoped = await fetchJson(`/api/allocations/subjects${qsTail ? `?${qsTail}` : ''}`, { headers });
        subjectsArray = Array.isArray(allocScoped) ? allocScoped : (allocScoped?.rows || allocScoped?.subjects || allocScoped?.data || []);
      } catch (allocErr) {
        console.warn('[SubjectManagementPage] Scoped allocations fetch failed', allocErr);
      }
      if (!Array.isArray(subjectsArray) || subjectsArray.length === 0) {
        try {
          const subjectsScoped = await fetchJson(`/api/subjects?include=category${qsTail ? `&${qsTail}` : ''}`, { headers });
          subjectsArray = Array.isArray(subjectsScoped) ? subjectsScoped : (subjectsScoped?.subjects || subjectsScoped?.rows || subjectsScoped?.data || []);
        } catch (subjectErr) {
          console.warn('[SubjectManagementPage] Scoped subjects fallback failed', subjectErr);
        }
      }
      subjectsArray = filterByPhaseContext(subjectsArray);
      if (isSecPhase) {
        try {
          const [departmentsResp, subjectGroupsResp] = await Promise.all([
            fetchJson('/api/departments', { headers }).catch(() => []),
            fetchJson('/api/subject-groups?includeVariants=0', { headers }).catch(() => [])
          ]);
          const departmentsArray = Array.isArray(departmentsResp) ? departmentsResp : (departmentsResp?.rows || departmentsResp?.data || []);
          const subjectGroupsArray = Array.isArray(subjectGroupsResp) ? subjectGroupsResp : (subjectGroupsResp?.rows || subjectGroupsResp?.data || []);
          const departmentById = new Map((departmentsArray || []).map((department) => [String(department.id), department]));
          const subjectGroupById = new Map((subjectGroupsArray || []).map((group) => [String(group.id), group]));
          const userById = new Map((Array.isArray(usersArray) ? usersArray : []).map((user) => [String(user.id), user]));

          subjectsArray = subjectsArray.map((subject) => {
            const groupId = subject?.subjectGroupId || subject?.group?.id || null;
            const group = groupId ? subjectGroupById.get(String(groupId)) : null;
            const departmentId = group?.departmentId || null;
            const department = departmentId ? departmentById.get(String(departmentId)) : null;
            const departmentHeadId = department?.subjectHeadId || null;
            const departmentHead = departmentHeadId ? userById.get(String(departmentHeadId)) : null;
            return {
              ...subject,
              departmentName: department?.name || null,
              departmentHeadName: departmentHead ? getDisplayName(departmentHead) : null
            };
          });
        } catch (_) {}
      }
      // Debug the final URLs used when empty to spot query construction issues
      try {
        if ((subjectsArray?.length || 0) === 0 || (classesArray?.length || 0) === 0 || (teachersArray?.length || 0) === 0) {
          console.debug('[SubjectManagementPage] urls', {
            classesLite: `/api/classes-lite${qsTail ? `?${qsTail}` : ''}`,
            classes: `/api/classes${qsTail ? `?${qsTail}` : ''}`,
            users: `/api/users${qsTail ? `?${qsTail}` : ''}`,
            subjectsAlloc: `/api/allocations/subjects`,
            subjects: `/api/subjects?include=category${qsTail ? `&${qsTail}` : ''}`
          });
        }
      } catch(_) {}
      try {
        if ((subjectsArray?.length || 0) === 0 || (classesArray?.length || 0) === 0 || (teachersArray?.length || 0) === 0) {
          console.debug('[SubjectManagementPage] debug', {
            headersUsed: headers,
            queryTail: qsTail,
            counts: { subjects: subjectsArray?.length || 0, classes: classesArray?.length || 0, teachers: teachersArray?.length || 0 }
          });
        }
      } catch(_) {}
      // Sort subjects for recent display: prefer createdAt desc, then id desc
      try {
        subjectsArray = [...subjectsArray].sort((a, b) => {
          const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          if (bd !== ad) return bd - ad;
          const aid = Number(a.id || 0);
          const bid = Number(b.id || 0);
          return bid - aid;
        });
      } catch(_) {}
      setSubjects(subjectsArray);
      setClasses(classesArray);
      setTeachers(teachersArray);
      try { localStorage.setItem(kSubjects, JSON.stringify(subjectsArray)); } catch(_) {}
      try { localStorage.setItem(kClasses, JSON.stringify(classesArray)); } catch(_) {}
      try { localStorage.setItem(kTeachers, JSON.stringify(teachersArray)); } catch(_) {}
      setLoading(false);
      try { clearTimeout(maxGuard); } catch(_) {}
    } catch (error) {
      console.error('Error fetching overview data:', error);
      setError(t('subjectManagement.errors.fetchOverviewData', { message: error.message }));
      setLoading(false);
    }
  };

  useScopeAutoRefresh([fetchOverviewData]);

  const quickActions = [
    {
      title: t('subjectManagement.quickActions.addNewSubject.title'),
      description: t('subjectManagement.quickActions.addNewSubject.description'),
      icon: <AddIcon />,
      color: 'primary',
      path: '/subject-management/add-subject'
    },
    {
      title: t('subjectManagement.quickActions.manageSubjects.title'),
      description: t('subjectManagement.quickActions.manageSubjects.description'),
      icon: <SubjectIcon />,
      color: 'secondary',
      path: '/subject-management/manage-subjects'
    },
    ...(isSecPhase ? [
      {
        title: t('subjectManagement.quickActions.subjectAssignments.title'),
        description: t('subjectManagement.quickActions.subjectAssignments.description'),
        icon: <AssignmentIcon />,
        color: 'info',
        path: '/subject-management/subject-assignments'
      }
    ] : []),
    {
      title: t('subjectManagement.quickActions.subjectReports.title'),
      description: t('subjectManagement.quickActions.subjectReports.description'),
      icon: <AssessmentIcon />,
      color: 'success',
      path: '/subject-management/subject-reports'
    }
  ];

  // Only block when we truly have nothing to display yet
  if (loading && subjects.length === 0 && classes.length === 0 && teachers.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <AppLoader variant="circular3" size={60} centered minHeight="60vh" messages={[]} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            {t('subjectManagement.title')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('subjectManagement.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SubjectIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('subjectManagement.overview.totalSubjects')}
                  </Typography>
                  <Typography variant="h4">
                    {subjects.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('subjectManagement.overview.totalTeachers')}
                  </Typography>
                  <Typography variant="h4">
                    {teachers.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('subjectManagement.overview.activeSubjects')}
                  </Typography>
                  <Typography variant="h4">
                    {subjects.filter(subject => subject.isActive !== false).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('subjectManagement.sections.quickActions')}
        </Typography>
        <Grid container spacing={2}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => navigate(action.path)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      color: `${action.color}.main`, 
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {action.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {action.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Recent Subjects */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('subjectManagement.sections.recentSubjects')}
        </Typography>
        {subjects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              {t('subjectManagement.recentSubjects.empty')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/subject-management/add-subject')}
              sx={{ mt: 2 }}
            >
              {t('subjectManagement.recentSubjects.addFirstSubject')}
            </Button>
          </Box>
        ) : (
          <List>
            {subjects.slice(0, 5).map((subject, index) => (
              <React.Fragment key={subject.id}>
                <ListItem>
                  <ListItemIcon>
                    <SubjectIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    // ListItemText wraps `primary` in a Typography span by
                    // default, and a Box renders a div — invalid nesting, which
                    // React warns about and some builds strip.
                    primaryTypographyProps={{ component: 'div' }}
                    primary={
                      // Every phase runs two levels and each has its own
                      // subjects, so the name alone is ambiguous — secondary
                      // has Physics at first cycle and at second cycle. The
                      // secondary branch showed no level at all, which made
                      // those two rows identical on screen.
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <span>{subject.name}</span>
                        {subject?.level && (
                          <Chip label={levelLabel(subject.level)} size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={isSecPhase
                      ? t('subjectManagement.recentSubjects.secondary', {
                          department: subject?.departmentName || t('classManagement.manage.na'),
                          subjectHead: subject?.departmentHeadName || t('subjectReports.labels.notAssigned')
                        })
                      : subject?.category?.name || t('classManagement.manage.na')
                    }
                  />
                  <Chip 
                    label={subject.isActive !== false ? t('status.active') : t('status.inactive')} 
                    color={subject.isActive !== false ? 'success' : 'default'}
                    size="small"
                  />
                </ListItem>
                {index < Math.min(4, subjects.length - 1) && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default SubjectManagementPage;
